import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { FileUploader } from 'components/common/uploader';

import { FormApi } from 'informed';

interface Props {
  updateFiles: () => Promise<void>;
}

interface IFileUploader {
  upload: (sendNotice?: boolean) => Promise<{
    extension: string;
    filename: string;
    url: string;
  }>;
}

const CalendarEventAddAttachment = ({ updateFiles }: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);
  const uploader = useRef<IFileUploader>(undefined);

  const [hasFile, setHasFile] = useState(false);

  const handleFileAdded = ({ filename }: { filename: string }) => {
    formApi.current.setValue('filename', filename.split('.')[0]);
    setHasFile(true);
  };

  const handleFileRemoved = () => {
    formApi.current.setValue('filename', null);
    setHasFile(false);
  };

  const handleSubmit = async () => {
    if (!hasFile) return true;

    try {
      const result = await uploader.current.upload();
      if (result) {
        const urlParts = result.url.split('/');

        await api.post(
          `/admin/calendar-events/${calendarEvent.id}/attachments`,
          {
            extension: result.extension,
            filename: urlParts[urlParts.length - 1],
            fileUrl: result.url
          }
        );

        dispatch.addNotice({
          text: messages.eventCalendarAttachmentAddSuccess,
          type: 'success'
        });

        await updateFiles();
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.eventCalendarAttachmentAdd}
      modalActionText={messages.commonAdd}
      modalTitle={messages.eventCalendarAttachmentAdd}
      onHide={() => setHasFile(false)}
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi) => (formApi.current = form)}
      submitDisabled={!hasFile}
    >
      {() => (
        <div>
          <FileUploader
            dragDrop={true}
            dragDropActions={false}
            getUploader={(fileUploader: IFileUploader) =>
              (uploader.current = fileUploader)
            }
            icon="file"
            id={`file-uploader`}
            keyPrefix="uploads/files/"
            onFileAdded={handleFileAdded}
            onFileRemoved={handleFileRemoved}
            uuidAsFilename={false}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventAddAttachment;
